import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useCustomerAuth } from "@/components/customer/CustomerAuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Clock, Tag, Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { TableOfContents } from "@/components/customer/TableOfContents";
import { ArticleRating } from "@/components/customer/ArticleRating";
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

type PortalArticle = Tables<'portal_articles'>;

// Content type detection utility
const detectContentType = (content: string): 'html' | 'markdown' | 'text' => {
  const trimmedContent = content.trim();
  
  // Check for HTML tags
  const htmlTagPattern = /<\s*([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/;
  if (htmlTagPattern.test(trimmedContent)) {
    return 'html';
  }
  
  // Check for common Markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s+.+$/m,     // Headers
    /^\*\s+.+$/m,         // Bullet lists
    /^\d+\.\s+.+$/m,      // Numbered lists
    /\*\*.*?\*\*/,        // Bold
    /\*.*?\*/,            // Italic
    /\[.*?\]\(.*?\)/,     // Links
  ];
  
  if (markdownPatterns.some(pattern => pattern.test(trimmedContent))) {
    return 'markdown';
  }
  
  return 'text';
};

// Content renderer component
const ContentRenderer = ({ content, contentType }: { content: string; contentType: 'html' | 'markdown' | 'text' }) => {
  if (contentType === 'html') {
    const sanitizedContent = DOMPurify.sanitize(content);
    return (
      <div 
        className="prose prose-lg max-w-none [&_a]:text-primary hover:[&_a]:text-primary/80"
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
    );
  }
  
  if (contentType === 'markdown') {
    return (
      <div className="prose prose-lg max-w-none [&_a]:text-primary hover:[&_a]:text-primary/80">
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    );
  }
  
  // Plain text
  return (
    <div className="prose prose-lg max-w-none">
      <div style={{ whiteSpace: 'pre-wrap' }}>
        {content}
      </div>
    </div>
  );
};

export default function CustomerHelpArticle() {
  const { slug } = useParams<{ slug: string }>();
  const { customer, loading: authLoading } = useCustomerAuth();
  const { toast } = useToast();
  const [article, setArticle] = useState<PortalArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState({ helpful: 0, notHelpful: 0 });
  const [contentType, setContentType] = useState<'html' | 'markdown' | 'text'>('markdown');

  useEffect(() => {
    if (authLoading) return;
    
    if (!customer) {
      toast({
        title: "Authentication required",
        description: "Please log in to view help articles.",
        variant: "destructive",
      });
      return;
    }

    if (slug) {
      loadArticle();
    }
  }, [authLoading, customer, slug]);

  const loadArticle = async () => {
    if (!slug || !customer) return;
    
    try {
      // Use edge function to fetch article details
      const { data, error } = await supabase.functions.invoke('customer-portal-article-detail', {
        body: {
          slug,
          customer_email: customer.email,
          customer_id: customer.id,
          session_token: 'authenticated' // Since we're already authenticated
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to load article');
      }

      if (!data?.article) {
        throw new Error('Article not found');
      }

      setArticle(data.article);
      setRatings(data.ratings || { helpful: 0, notHelpful: 0 });
      // Use explicit content_type if available, otherwise auto-detect
      const explicitType = (data.article as any).content_type;
      setContentType(explicitType && ['html', 'markdown', 'text'].includes(explicitType) 
        ? explicitType 
        : detectContentType(data.article.content));
    } catch (error) {
      console.error('Error loading article:', error);
      toast({
        title: "Article not found",
        description: "The requested help article could not be found.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full" />
        <div className="grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3 space-y-4">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-6 w-full" />
          </div>
          <div className="hidden lg:block">
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
        <p className="text-muted-foreground mb-6">
          The help article you're looking for doesn't exist or has been removed.
        </p>
        <Button asChild>
          <Link to="/help">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Help Center
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/dashboard">Portal</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/help">Help & Tips</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{article.title}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Back Button */}
      <Button variant="ghost" asChild className="mb-6">
        <Link to="/help">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Help Center
        </Link>
      </Button>

      <div className="grid lg:grid-cols-4 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Article Header */}
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-foreground">{article.title}</h1>
            
            {article.excerpt && (
              <p className="text-xl text-muted-foreground leading-relaxed">
                {article.excerpt}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              {article.published_at && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>Published {new Date(article.published_at).toLocaleDateString()}</span>
                </div>
              )}
              
              {article.category && (
                <div className="flex items-center gap-1">
                  <Tag className="h-4 w-4" />
                  <span>{article.category}</span>
                </div>
              )}
            </div>
          </div>

          {/* Mobile ToC */}
          <div className="lg:hidden">
            <TableOfContents content={article.content} mobile />
          </div>

          {/* Article Content */}
          <Card>
            <CardContent className="p-8">
              <ContentRenderer content={article.content} contentType={contentType} />
            </CardContent>
            
            {/* Rating Component */}
            <ArticleRating
              articleId={article.id}
              organizationId={article.organization_id}
              initialHelpfulCount={ratings.helpful}
              initialNotHelpfulCount={ratings.notHelpful}
            />
          </Card>
        </div>

        {/* Desktop ToC Sidebar */}
        <div className="hidden lg:block">
          <TableOfContents content={article.content} />
        </div>
      </div>
    </div>
  );
}